import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  History, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  Loader2, RefreshCw, Filter, Calendar, Clock, DollarSign,
  Activity, BarChart3, Wallet
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TradingHistoryPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const [ordersRes, positionsRes] = await Promise.all([
        fetch(`${API_URL}/api/orderbook/user/orders`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/orderbook/user/positions`, { headers, credentials: 'include' })
      ]);

      const [ordersData, positionsData] = await Promise.all([
        ordersRes.json(),
        positionsRes.json()
      ]);

      setOrders(ordersData.orders || []);
      setPositions(positionsData.positions || []);
    } catch (error) {
      console.error('Failed to fetch trading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = statusFilter === 'all' 
    ? orders 
    : orders.filter(o => o.status === statusFilter);

  // Calculate stats
  const totalTrades = orders.filter(o => o.status === 'filled').length;
  const openOrders = orders.filter(o => o.status === 'open').length;
  const totalPositionValue = positions.reduce((sum, p) => sum + (p.shares * 50), 0); // Assuming 50¢ avg

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#3B82F6] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810]">
      <Header />
      
      {/* Header */}
      <div className="bg-gradient-to-b from-[#131b2e] to-[#050810] border-b border-[#1e2a45]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <History className="h-6 w-6 text-[#3B82F6]" />
                Trading History
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                View your orders, trades, and positions
              </p>
            </div>
            <Button
              onClick={fetchData}
              variant="outline"
              className="border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45]"
              data-testid="refresh-history"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="h-5 w-5 text-[#3B82F6]" />
              <span className="text-sm text-gray-400">Total Trades</span>
            </div>
            <p className="text-2xl font-bold text-white">{totalTrades}</p>
          </div>
          
          <div className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-5 w-5 text-amber-400" />
              <span className="text-sm text-gray-400">Open Orders</span>
            </div>
            <p className="text-2xl font-bold text-white">{openOrders}</p>
          </div>
          
          <div className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-4">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="h-5 w-5 text-green-400" />
              <span className="text-sm text-gray-400">Active Positions</span>
            </div>
            <p className="text-2xl font-bold text-white">{positions.length}</p>
          </div>
          
          <div className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="h-5 w-5 text-purple-400" />
              <span className="text-sm text-gray-400">Positions Value</span>
            </div>
            <p className="text-2xl font-bold text-white">{totalPositionValue}¢</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'orders'
                ? 'bg-[#3B82F6] text-white'
                : 'bg-[#1e2a45] text-gray-300 hover:bg-[#2d3a56]'
            }`}
          >
            Orders ({orders.length})
          </button>
          <button
            onClick={() => setActiveTab('positions')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'positions'
                ? 'bg-[#3B82F6] text-white'
                : 'bg-[#1e2a45] text-gray-300 hover:bg-[#2d3a56]'
            }`}
          >
            Positions ({positions.length})
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 text-[#3B82F6] animate-spin" />
          </div>
        ) : (
          <>
            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] overflow-hidden">
                {/* Filter */}
                <div className="p-4 border-b border-[#1e2a45] flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-400">Status:</span>
                  {['all', 'open', 'filled', 'cancelled'].map(status => (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      className={`px-3 py-1 text-xs rounded-full transition-colors ${
                        statusFilter === status
                          ? 'bg-[#3B82F6] text-white'
                          : 'bg-[#1e2a45] text-gray-400 hover:bg-[#2d3a56]'
                      }`}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>

                {/* Orders Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b border-[#1e2a45]">
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Market</th>
                        <th className="px-4 py-3">Side</th>
                        <th className="px-4 py-3">Outcome</th>
                        <th className="px-4 py-3">Price</th>
                        <th className="px-4 py-3">Quantity</th>
                        <th className="px-4 py-3">Filled</th>
                        <th className="px-4 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                            No orders found
                          </td>
                        </tr>
                      ) : (
                        filteredOrders.map(order => (
                          <tr key={order.id} className="border-b border-[#1e2a45] hover:bg-[#1e2a45]/30">
                            <td className="px-4 py-3 text-sm text-gray-400">
                              {new Date(order.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-white max-w-[200px] truncate">
                              {order.market_id}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`flex items-center gap-1 text-sm font-medium ${
                                order.side === 'buy' ? 'text-green-400' : 'text-red-400'
                              }`}>
                                {order.side === 'buy' ? (
                                  <ArrowUpRight className="h-3 w-3" />
                                ) : (
                                  <ArrowDownRight className="h-3 w-3" />
                                )}
                                {order.side.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-sm font-medium ${
                                order.outcome === 'YES' ? 'text-[#3B82F6]' : 'text-[#F97316]'
                              }`}>
                                {order.outcome}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-white">{order.price}¢</td>
                            <td className="px-4 py-3 text-sm text-white">{order.quantity}</td>
                            <td className="px-4 py-3 text-sm text-white">
                              {order.filled_quantity}/{order.quantity}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                order.status === 'filled' ? 'bg-green-500/20 text-green-400' :
                                order.status === 'open' ? 'bg-amber-500/20 text-amber-400' :
                                order.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>
                                {order.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Positions Tab */}
            {activeTab === 'positions' && (
              <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b border-[#1e2a45]">
                        <th className="px-4 py-3">Market</th>
                        <th className="px-4 py-3">Outcome</th>
                        <th className="px-4 py-3">Shares</th>
                        <th className="px-4 py-3">Avg Price</th>
                        <th className="px-4 py-3">Cost Basis</th>
                        <th className="px-4 py-3">Current Value</th>
                        <th className="px-4 py-3">P&L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {positions.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                            No positions found
                          </td>
                        </tr>
                      ) : (
                        positions.map(pos => {
                          const costBasis = pos.shares * pos.avg_price;
                          const currentValue = pos.shares * 50; // Assuming 50¢ current price
                          const pnl = currentValue - costBasis;
                          const pnlPercent = costBasis > 0 ? ((pnl / costBasis) * 100).toFixed(1) : 0;
                          
                          return (
                            <tr key={pos.id} className="border-b border-[#1e2a45] hover:bg-[#1e2a45]/30">
                              <td className="px-4 py-3 text-sm text-white max-w-[250px]">
                                <div className="truncate">{pos.market_title || pos.market_id}</div>
                                <div className="text-xs text-gray-500">{pos.market_status}</div>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-sm font-medium ${
                                  pos.outcome === 'YES' ? 'text-[#3B82F6]' : 'text-[#F97316]'
                                }`}>
                                  {pos.outcome}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-white">{pos.shares}</td>
                              <td className="px-4 py-3 text-sm text-white">{pos.avg_price}¢</td>
                              <td className="px-4 py-3 text-sm text-white">{costBasis}¢</td>
                              <td className="px-4 py-3 text-sm text-white">{currentValue}¢</td>
                              <td className="px-4 py-3">
                                <span className={`text-sm font-medium ${
                                  pnl >= 0 ? 'text-green-400' : 'text-red-400'
                                }`}>
                                  {pnl >= 0 ? '+' : ''}{pnl}¢ ({pnlPercent}%)
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default TradingHistoryPage;
